import axios from "axios";
import { OpenAPI } from "@propertelligent/client-api";

export interface DeletePaymentDueAccountGroupResult {
  candidateCount: number;
  deletedCount: number;
  skippedCount: number;
  message: string;
}

export interface PayPaymentDueAccountGroupResult {
  candidateCount: number;
  paidCount: number;
  skippedCount: number;
  message: string;
}

/** Per-id amount override for PUT /pay/group — omit entry = full outstanding for that id. */
export interface BulkPayAmount {
  paymentDueAccountId: number;
  amount: number;
}

export interface BulkPayRequest {
  paymentDueAccountIds: number[];
  /** Partial (or explicit) amounts for single-id rows; empty/omit = full payoff for all ids. */
  amounts?: BulkPayAmount[];
  fromPaymentMethodId?: number;
  paymentMethodTypeId: number; // PaymentMethodTypesEnum
  checkNumber?: string;
  paymentDate: string; // ISO 8601
  transactionId?: string;
  assistanceProvider?: string;
}

export type PartialPayModeState = {
  mode: "full" | "partial" | null;
  partialAmount?: number;
};

export interface PaymentDeleteOutcome {
  shouldRefresh: boolean;
  notificationType: "success" | "warning";
}

export interface SingleItemResponse<T> {
  success?: T;
  errors?: Record<string, string[]>;
}

export interface GroupedPaymentDueAccountLike {
  id?: number;
  paymentDueAccountIds?: number[];
}

export interface ApiHelperResponse<T> {
  data?: T | null;
  errors?: unknown;
  statusCode?: number | null;
}

export type DeletePaymentActionStatus = "success" | "failure";

export interface DeletePaymentAction {
  status: DeletePaymentActionStatus;
  shouldRefresh: boolean;
  notificationType: "success" | "warning" | "failure";
  message?: string;
}

const baseUrl = (): string =>
  typeof OpenAPI.BASE === "string" ? OpenAPI.BASE : "";

const authHeaders = (): Record<string, string> => {
  const token =
    typeof OpenAPI.TOKEN === "string" ? OpenAPI.TOKEN : undefined;
  return token ? { Authorization: ["Bearer", token].join(" ") } : {};
};

export const deletePaymentDueAccountGroup = async (
  paymentDueAccountIds: number[],
): Promise<SingleItemResponse<DeletePaymentDueAccountGroupResult>> => {
  const response = await axios.delete(
    `${baseUrl()}/api/PaymentDueAccount/group`,
    {
      data: { paymentDueAccountIds },
      headers: authHeaders(),
    },
  );
  return response.data;
};

export const getPaymentDeleteOutcome = (
  result: DeletePaymentDueAccountGroupResult,
): PaymentDeleteOutcome => ({
  shouldRefresh: result.deletedCount > 0,
  notificationType:
    result.deletedCount > 0 && result.skippedCount === 0
      ? "success"
      : "warning",
});

export const payPaymentDueAccountGroup = async (
  request: BulkPayRequest,
): Promise<SingleItemResponse<PayPaymentDueAccountGroupResult>> => {
  const response = await axios.put(
    `${baseUrl()}/api/PaymentDueAccount/pay/group`,
    request,
    { headers: authHeaders() },
  );
  return response.data;
};

export const getPaymentPayOutcome = (
  result: PayPaymentDueAccountGroupResult,
): PaymentDeleteOutcome => ({
  // Reuses the existing {shouldRefresh, notificationType} shape -- same semantics as
  // getPaymentDeleteOutcome, with paidCount in place of deletedCount.
  shouldRefresh: result.paidCount > 0,
  notificationType:
    result.paidCount > 0 && result.skippedCount === 0 ? "success" : "warning",
});

export const resolveGroupedPaymentIds = (
  payment: GroupedPaymentDueAccountLike,
): number[] =>
  (payment.paymentDueAccountIds?.length
    ? payment.paymentDueAccountIds
    : [payment.id]
  ).filter((paymentId): paymentId is number => Number(paymentId) > 0);

/**
 * Builds `amounts` overrides for settle bulk-pay: only partial mode + exactly one
 * underlying PDA id. Multi-id grouped rows never get an override (full payoff).
 */
export const buildPartialBulkPayAmounts = (
  selectedPayments: Array<GroupedPaymentDueAccountLike & { amount?: number }>,
  paymentStates: Record<string, PartialPayModeState>,
): BulkPayAmount[] => {
  const amounts: BulkPayAmount[] = [];
  for (const payment of selectedPayments) {
    const ids = resolveGroupedPaymentIds(payment);
    if (ids.length !== 1) continue;
    const state = paymentStates[String(payment.id)];
    if (state?.mode !== "partial") continue;
    const amount = Number(
      payment.amount ?? state.partialAmount ?? 0,
    );
    if (!(amount > 0)) continue;
    amounts.push({ paymentDueAccountId: ids[0], amount });
  }
  return amounts;
};

export const mapDeleteResponseToAction = (
  apiHelperResponse:
    | ApiHelperResponse<SingleItemResponse<DeletePaymentDueAccountGroupResult>>
    | null
    | undefined,
): DeletePaymentAction => {
  if (
    !apiHelperResponse ||
    apiHelperResponse.statusCode ||
    apiHelperResponse.errors
  ) {
    return {
      status: "failure",
      shouldRefresh: true,
      notificationType: "failure",
    };
  }

  const result = apiHelperResponse.data?.success;
  if (!result) {
    return {
      status: "failure",
      shouldRefresh: true,
      notificationType: "failure",
    };
  }

  const outcome = getPaymentDeleteOutcome(result);
  return {
    status: "success",
    shouldRefresh: outcome.shouldRefresh,
    notificationType: outcome.notificationType,
    message: result.message,
  };
};

export const mapPayResponseToAction = (
  apiHelperResponse:
    | ApiHelperResponse<SingleItemResponse<PayPaymentDueAccountGroupResult>>
    | null
    | undefined,
): DeletePaymentAction => {
  if (
    !apiHelperResponse ||
    apiHelperResponse.statusCode ||
    apiHelperResponse.errors
  ) {
    return {
      status: "failure",
      shouldRefresh: true,
      notificationType: "failure",
    };
  }

  const result = apiHelperResponse.data?.success;
  if (!result) {
    return {
      status: "failure",
      shouldRefresh: true,
      notificationType: "failure",
    };
  }

  const outcome = getPaymentPayOutcome(result);
  return {
    status: "success",
    shouldRefresh: outcome.shouldRefresh,
    notificationType: outcome.notificationType,
    message: result.message,
  };
};
